import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { Tenant } from '@packages/shared-types';
import { getMyTenant, getTenantById } from '@/services/tenantService';
import { useAuth } from '@/hooks/useAuth';
import {
  getActiveTenantId,
  setActiveTenantId,
  clearActiveTenantId,
} from '@/services/tenantStorage';
import { setImpersonatedTenantId } from '@/services/impersonationContext';

interface TenantSwitcherContextValue {
  activeTenant: Tenant | null;
  switchToTenant: (tenant: Tenant) => void;
  clearActiveTenant: () => void;
  loadingTenant: boolean;
}

const TenantSwitcherContext = createContext<TenantSwitcherContextValue | undefined>(
  undefined,
);

export function TenantSwitcherProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const { user, isLoading: isLoadingUser } = useAuth();
  const [activeTenant, setActiveTenant] = useState<Tenant | null>(null);
  const [loadingTenant, setLoadingTenant] = useState(true);

  useEffect(() => {
    let isMounted = true;
    
    // Wait for user to load
    if (isLoadingUser) {
      return;
    }
    
    if (!user) {
      setActiveTenant(null);
      clearActiveTenantId();
      setImpersonatedTenantId(null);
      setLoadingTenant(false);
      return;
    }

    // For root users: check localStorage for persisted tenant
    if (user.role === 'root') {
      const persistedTenantId = getActiveTenantId();
      if (persistedTenantId) {
        setLoadingTenant(true);
        void getTenantById(persistedTenantId)
          .then((tenant) => {
            if (isMounted) {
              setActiveTenant(tenant);
              setImpersonatedTenantId(tenant.id);
            }
          })
          .catch(() => {
            // If tenant not found, clear from storage
            if (isMounted) {
              setActiveTenant(null);
              clearActiveTenantId();
              setImpersonatedTenantId(null);
            }
          })
          .finally(() => {
            if (isMounted) {
              setLoadingTenant(false);
            }
          });
      } else {
        setActiveTenant(null);
        setImpersonatedTenantId(null);
        setLoadingTenant(false);
      }
      return;
    }

    // For non-root users: load their tenant via /me (allowed for admin and user roles)
    if (!user.tenantId) {
      setActiveTenant(null);
      setLoadingTenant(false);
      return;
    }

    setLoadingTenant(true);
    void getMyTenant()
      .then((tenant) => {
        if (isMounted) {
          setActiveTenant(tenant);
          setImpersonatedTenantId(tenant.id);
        }
      })
      .catch(() => {
        if (isMounted) {
          setActiveTenant(null);
          setImpersonatedTenantId(null);
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoadingTenant(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [user]);

  const switchToTenant = useCallback(
    (tenant: Tenant) => {
      setActiveTenant(tenant);
      setImpersonatedTenantId(tenant.id);
      // Only persist for root users
      if (user?.role === 'root') {
        setActiveTenantId(tenant.id);
      }
    },
    [user],
  );

  const clearActiveTenant = useCallback(() => {
    setActiveTenant(null);
    setImpersonatedTenantId(null);
    // Only clear storage for root users
    if (user?.role === 'root') {
      clearActiveTenantId();
    }
  }, [user]);

  const value = useMemo(
    () => ({
      activeTenant,
      switchToTenant,
      clearActiveTenant,
      loadingTenant,
    }),
    [activeTenant, clearActiveTenant, loadingTenant, switchToTenant],
  );

  return (
    <TenantSwitcherContext.Provider value={value}>
      {children}
    </TenantSwitcherContext.Provider>
  );
}

export function useTenantSwitcher(): TenantSwitcherContextValue {
  const context = useContext(TenantSwitcherContext);
  if (!context) {
    throw new Error(
      'useTenantSwitcher must be used within a TenantSwitcherProvider',
    );
  }
  return context;
}
